import { apiClient } from './client';
import type { HausListItem, Einheit } from '../types';
import type { PaginatedResponse } from '../types/api';

export interface HaeuserQueryParams {
  page?: number;
  per_page?: number;
  search_text?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  include_hidden?: boolean;
}

export type HausListResponse = PaginatedResponse<HausListItem>;

export interface HausUpdateData {
  typ?: string | null;
  bezeichnung?: string | null;
  baugenehmigung?: string | null;
  notes?: string | null;
}

export interface HausDetail {
  id: number;
  user_id: number;
  adresse_id: number;
  typ: string | null;
  bezeichnung: string | null;
  baugenehmigung: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  einheiten: (Einheit & { registrierungsnummer: string | null })[];
}

export interface HausVerfahren {
  id: number;
  aktenzeichen: string | null;
  staat: string;
  einheit_bezeichnung: string | null;
}

export interface HausInserat {
  id: number;
  title: string;
  platform: string;
  state: string;
  listing_url: string;
  is_online: boolean;
}

export interface HausVermieter {
  id: number;
  name: string;
  typ: string | null;
  email: string | null;
  telefon: string | null;
}

export const HAUS_TYPEN = [
  'Einfamilienhaus',
  'Mehrfamilienhaus',
  'Reihenhaus / Doppelhaus',
  'Hochhaus / Wohnblock',
  'Sonderform',
] as const;

export const haeuserApi = {
  getHaeuser: async (params: HaeuserQueryParams = {}): Promise<HausListResponse> => {
    const { data } = await apiClient.get('/haeuser/', { params });
    return data;
  },

  getHaus: async (id: number): Promise<HausDetail> => {
    const { data } = await apiClient.get(`/haeuser/${id}`);
    return data;
  },

  updateHaus: async (id: number, updates: HausUpdateData): Promise<HausDetail> => {
    const { data } = await apiClient.patch(`/haeuser/${id}`, updates);
    return data;
  },

  createHaus: async (body: { adresse_id: number; typ: string; bezeichnung?: string; baugenehmigung?: string; notes?: string }): Promise<HausDetail> => {
    const { data } = await apiClient.post('/haeuser/', body);
    return data;
  },

  deleteHaus: async (id: number): Promise<void> => {
    await apiClient.delete(`/haeuser/${id}`);
  },

  getVerfahrenAtHaus: async (hausId: number): Promise<HausVerfahren[]> => {
    const { data } = await apiClient.get(`/haeuser/${hausId}/verfahren`);
    return data;
  },

  getInserate: async (hausId: number): Promise<HausInserat[]> => {
    const { data } = await apiClient.get(`/haeuser/${hausId}/inserate`);
    return data;
  },

  getVermieter: async (hausId: number): Promise<HausVermieter[]> => {
    const { data } = await apiClient.get(`/haeuser/${hausId}/vermieter`);
    return data;
  },
};
